package com.htmlagent.site.state.brief

/**
 * What the assistant has gathered so far.
 *
 * **Nothing here is required.** Every field carries the value that was preselected, and [open]
 * records which questions the person deliberately left alone: those contribute nothing to the
 * prompt and the agent decides them exactly as it does today. That is the difference between an
 * assistant and a form — a form refuses to move until you fill it in, and this one only ever
 * adds what you actually said.
 */
data class Brief(
    val kind: PageKind = PageKind.default,
    val what: String = "",
    val action: CallToAction = PageKind.default.actions.first(),
    val register: Register = Register.default,
    val accent: String = DEFAULT_ACCENT,
    val pairing: Int = 0,
    val gestures: List<Gesture> = emptyList(),
    val imagery: ImagerySource = ImagerySource.Built,
    val treatment: ImageryTreatment = ImageryTreatment.Veiled,
    val references: String = "",
    val open: Set<BriefStep> = emptySet(),
) {

    /** Whether a step contributed anything, which is what decides if it reaches the prompt. */
    fun decided(step: BriefStep): Boolean = step !in open

    /** The chosen pairing, clamped: changing register leaves the old index pointing anywhere. */
    val typePairing: TypePairing get() = register.pairings[pairing.coerceIn(register.pairings.indices)]

    /** The seed, or `null` while somebody is halfway through typing a hex. */
    val seed: Oklch? get() = Palette.parse(accent)

    /**
     * Changing the kind of page can leave the chosen action orphaned: a waiting screen cannot
     * ask anybody to book a table. The action falls back to the first the new kind allows.
     */
    fun withKind(next: PageKind): Brief = decide(BriefStep.Commission).copy(
        kind = next,
        action = if (action in next.actions) action else next.actions.first(),
    )

    /** A register brings its own pairings and its own budget, so both selections start over. */
    fun withRegister(next: Register): Brief =
        decide(BriefStep.Register).copy(register = next, pairing = 0, gestures = emptyList())

    /**
     * Which gestures cannot be added right now.
     *
     * With a budget of one it is composition **or** behaviour. With a budget of two the second
     * has to be of the other kind: two animations are one gesture repeated, not two gestures.
     */
    fun blockedGestures(): Set<Gesture> {
        if (gestures.size >= register.budget.gestures) {
            return Gesture.entries.filterNot { it in gestures }.toSet()
        }
        val taken = gestures.map { it.kind }.toSet()
        return Gesture.entries.filter { it.kind in taken && it !in gestures }.toSet()
    }

    /** Adds or removes a gesture, refusing silently the ones the budget does not allow. */
    fun toggleGesture(gesture: Gesture): Brief = when {
        gesture in gestures -> decide(BriefStep.Form).copy(gestures = gestures - gesture)
        gesture in blockedGestures() -> this
        else -> decide(BriefStep.Form).copy(gestures = gestures + gesture)
    }

    /** Marks a step as answered. Every choice goes through here, so choosing opts back in. */
    fun decide(step: BriefStep): Brief = if (step in open) copy(open = open - step) else this

    /** Leaves a step alone. What it would have contributed is left for the agent to decide. */
    fun leaveOpen(step: BriefStep): Brief = copy(open = open + step)

    companion object {
        /**
         * The colour the assistant starts from.
         *
         * A neutral placeholder would be the honest default, and it is the wrong one: the point
         * of the step is to show a scale being derived, and a grey seed derives twelve greys.
         */
        const val DEFAULT_ACCENT = "#8A4B2A"
    }
}
